import { Box, Typography } from '@mui/material';
import { useRef, type MouseEvent } from 'react';

import {
  useBoardEditingStore,
  useBoardDragPreviewStore,
  useBoardSelectionStore,
  useWallContentStore,
  useWorkspaceUiStore,
  useWallParticipantsStore,
  type BoardElementType,
  type WallBoard,
  type WallCard,
} from '@walls/store';
import { useBoardViewportRef } from '../../viewport/BoardViewportContext';
import { BoardCard } from '../../cards/BoardCard';
import { ClusterChildList } from '../ClusterChildList';
import { ClusterChildDragHelper } from '../ClusterChildDragHelper';
import { ClusterResizeHandles } from '../ClusterResizeHandles';
import { CollapsedClusterFooter } from '../CollapsedClusterFooter';
import { useClusterCommands } from '../hooks/useClusterCommands';
import { useClusterChildSorting } from '../hooks/useClusterChildSorting';
import {
  isPointInClusterAddSurface,
  isPointInVisibleClusterSurface,
} from '../utils/clusterSurfaceHitTesting';
import { useConnectionCommands } from '../../connections/useConnectionCommands';
import { useCardDrag } from '../../cards/hooks/useCardDrag';
import { useCardPermissions } from '../../cards/hooks/useCardPermissions';
import { useCardResize } from '../../cards/hooks/useCardResize';
import { isCardVisibleForParticipantFilter } from '../../cards/utils/cardVisibility';
import { MultilineText } from '../../cards/utils/MultilineText';
import { useBoardElementRect } from '../../hooks/useBoardElementRect';
import { getClusterChildren, sortByZPosition } from '../../selectors/boardCardSelectors';
import { getPositionedCardDimensions } from '../../positioning/cardRects';
import { cardLayerBaseZIndex } from '../../positioning/zIndex';
import { getCardText, getCardType } from '../../utils/cardDisplay';

interface BoardClusterProps {
  board: WallBoard;
  cluster: WallCard;
  isClusterChild?: boolean;
  measureDependencies?: unknown[];
}

export function BoardCluster({
  board,
  cluster,
  isClusterChild = false,
  measureDependencies = [],
}: BoardClusterProps) {
  const elementRef = useRef<HTMLElement | null>(null);
  const cardsContainerRef = useRef<HTMLDivElement | null>(null);
  const cards = useWallContentStore((state) => state.cards);
  const dragPreview = useBoardDragPreviewStore((state) => state.cardDragPreviews[cluster.id]);
  const viewportRef = useBoardViewportRef();
  const currentElement = useBoardSelectionStore((state) => state.currentElement);
  const connectingFromId = useBoardSelectionStore((state) => state.connectingFromId);
  const currentParticipantId = useWallParticipantsStore((state) => state.currentParticipantId);
  const selectedIds = useBoardSelectionStore((state) => state.selectedIds);
  const setConnectingFromId = useBoardSelectionStore((state) => state.setConnectingFromId);
  const setCurrentElement = useBoardSelectionStore((state) => state.setCurrentElement);
  const visibleParticipantIds = useWorkspaceUiStore((state) => state.visibleParticipantIds);
  const selectedTool = useWorkspaceUiStore((state) => state.selectedTool);
  const zoom = useWorkspaceUiStore((state) => state.zoom);
  const startCreateCard = useBoardEditingStore((state) => state.startCreateCard);
  const isCurrent = currentElement?.id === cluster.id;
  const isSelected = selectedIds.includes(cluster.id);
  const dimensions = getPositionedCardDimensions(cluster);
  const { rootCards, rootClusters } = getClusterChildren(cluster);
  const orderedChildren = sortByZPosition([...rootClusters, ...rootCards]);
  const childCount = orderedChildren.length;
  const elementType: BoardElementType = 'cluster';
  const cardType = getCardType(cluster);
  const permissions = useCardPermissions(board, cluster);
  const isFocusFiltered = !isCardVisibleForParticipantFilter(cluster, visibleParticipantIds);
  const canSelect = permissions.canSelect && !isFocusFiltered;
  const canMove = permissions.canMove && !isFocusFiltered;
  const canResize = permissions.canResize && !isFocusFiltered;
  const canCreateCard = Boolean(
    board.allowCardCreation &&
      (board.isOwner || (currentParticipantId && (board.isMember || board.canCollaborate)))
  );
  const clusterCommands = useClusterCommands(board);
  const { insertConnection } = useConnectionCommands(board);
  const drag = useCardDrag({
    board,
    canMove,
    card: cluster,
    isClusterChild,
  });
  const resize = useCardResize({
    board,
    canResize: canResize && !cluster.collapsed,
    card: cluster,
    elementRef,
    isClusterChild,
  });
  useBoardElementRect(cluster.id, elementRef, [
    cluster.xPos,
    cluster.yPos,
    cluster.width,
    cluster.height,
    cluster.collapsed,
    cluster.cards?.length ?? 0,
    cluster.content,
    cluster.title,
    isClusterChild,
    ...measureDependencies,
  ]);
  const canSortClusterChildren = permissions.canSortClusterChildren;
  const isResizedCluster = cluster.width != null || cluster.height != null;
  const childMeasureDependencies = [
    ...measureDependencies,
    cluster.xPos,
    cluster.yPos,
    cluster.width,
    cluster.height,
    cluster.collapsed,
  ];
  const {
    activeClusterPlacementPreview,
    canSortChild,
    cardsContainerPointerHandlers,
    childSortDrag,
    draggedChild,
    getChildSortHandlers,
    showSourcePlacementPreview,
  } = useClusterChildSorting({
    board,
    canSortClusterChildren,
    cards,
    cardsContainerRef,
    cluster,
    clusterCommands,
    currentParticipantId,
    elementRef,
    isResizedCluster,
    orderedChildren,
    viewportRef,
    visibleParticipantIds,
    zoom,
  });

  const handleDoubleClick = (event: MouseEvent<HTMLElement>) => {
    if (event.target instanceof Element && event.target.closest('[data-resize-handle]')) {
      return;
    }

    const closestBoardElement =
      event.target instanceof Element
        ? event.target.closest<HTMLElement>('[data-element-id]')
        : null;

    if (closestBoardElement?.dataset.elementId !== cluster.id) {
      return;
    }

    const isAddSurfaceHit = isPointInClusterAddSurface(
      event.clientX,
      event.clientY,
      event.target,
      elementRef.current
    );

    if (
      !isAddSurfaceHit ||
      !canCreateCard ||
      (selectedTool !== 'pointer' && selectedTool !== 'card')
    ) {
      return;
    }

    event.stopPropagation();
    setCurrentElement({ id: cluster.id, type: elementType });
    startCreateCard('', cluster.id);
  };

  return (
    <Box
      ref={elementRef}
      id={`cluster-content-container_${cluster.id}`}
      data-element-id={cluster.id}
      data-element-type={elementType}
      onDoubleClick={handleDoubleClick}
      onPointerDown={(event) => {
        const isVisibleSurfaceHit =
          cluster.collapsed ||
          isPointInVisibleClusterSurface(
            event.clientX,
            event.clientY,
            event.target,
            elementRef.current
          );

        if (!isVisibleSurfaceHit) {
          return;
        }

        event.stopPropagation();
        if (!canSelect) {
          return;
        }

        if (connectingFromId) {
          if (connectingFromId === cluster.id) {
            setConnectingFromId(null);
          } else {
            void insertConnection(connectingFromId, cluster.id);
          }

          return;
        }

        setCurrentElement({ id: cluster.id, type: elementType });
        drag.onPointerDown(event);
      }}
      onPointerMove={drag.onPointerMove}
      onPointerUp={drag.onPointerUp}
      onPointerCancel={drag.onPointerCancel}
      sx={{
        bgcolor: cluster.collapsed ? cluster.color || '#ffff99' : 'rgba(255, 255, 255, 0.5)',
        boxShadow:
          isCurrent || isSelected
            ? '0 0 0 2px #7dc9fb'
            : cluster.isLocked
            ? 'none'
            : '0 1px 1px rgba(0,0,0,0.15)',
        color: '#111',
        cursor: canMove && !isClusterChild && !resize.isResizing ? 'move' : 'pointer',
        display: 'inline-block',
        height: cluster.collapsed ? undefined : cluster.height,
        left: isClusterChild ? undefined : cluster.xPos,
        minWidth: cluster.collapsed ? 220 : dimensions.width,
        position: isClusterChild ? 'relative' : 'absolute',
        top: isClusterChild ? undefined : cluster.yPos,
        transform: dragPreview
          ? `translate3d(${dragPreview.deltaX}px, ${dragPreview.deltaY}px, 0)`
          : undefined,
        userSelect: 'none',
        width: cluster.collapsed ? undefined : cluster.width,
        zIndex: isClusterChild
          ? 3
          : cluster.isLocked
          ? Math.max(cluster.zPos ?? 0, cardLayerBaseZIndex)
          : cluster.zPos ?? 3,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: cluster.collapsed || cluster.height == null ? undefined : '100%',
          minHeight: 0,
          position: 'relative',
        }}
      >
        <Typography
          className="title"
          variant="body2"
          sx={{
            bgcolor: cluster.color || '#ffff99',
            minHeight: 38,
            p: 1.25,
            wordBreak: 'break-word',
          }}
        >
          <MultilineText
            text={
              getCardText(cluster) || (cardType === 'text' ? 'Untitled cluster' : 'Image cluster')
            }
          />
        </Typography>
        {cluster.collapsed ? (
          <CollapsedClusterFooter childCount={childCount} isLocked={cluster.isLocked} />
        ) : (
          <ClusterChildList
            activeClusterPlacementPreview={activeClusterPlacementPreview}
            canSortChild={canSortChild}
            cardsContainerPointerHandlers={cardsContainerPointerHandlers}
            cardsContainerRef={cardsContainerRef}
            childCount={childCount}
            childSortDrag={childSortDrag}
            cluster={cluster}
            getChildSortHandlers={getChildSortHandlers}
            isResizedCluster={isResizedCluster}
            orderedChildren={orderedChildren}
            renderChild={(childCard) =>
              childCard.persistCluster || childCard.cards?.length ? (
                <BoardCluster
                  board={board}
                  cluster={childCard}
                  isClusterChild
                  measureDependencies={childMeasureDependencies}
                />
              ) : (
                <BoardCard
                  board={board}
                  card={childCard}
                  isClusterChild
                  measureDependencies={childMeasureDependencies}
                />
              )
            }
            showSourcePlacementPreview={showSourcePlacementPreview}
          />
        )}
        {resize.canResize && <ClusterResizeHandles getHandleProps={resize.getHandleProps} />}
        {childSortDrag && draggedChild && (
          <ClusterChildDragHelper
            board={board}
            child={draggedChild}
            drag={childSortDrag}
            zoom={zoom}
          />
        )}
      </Box>
    </Box>
  );
}
